import { createContext, useContext, type ReactNode } from 'react'
import type { IPipelineService } from './IPipelineService.interface'
import { MockPipelineService } from './MockPipelineService'
import { StridePipelineService } from './StridePipelineService'

// ── Service resolution ────────────────────────────────────────────────────────
// VITE_PIPELINE_SERVICE controls which service is used at runtime.
// Default: 'mock' for local development.
// Set to 'stride' in the Azure DevOps pipeline variable group.

function resolveService(): IPipelineService {
  const key = import.meta.env.VITE_PIPELINE_SERVICE ?? 'mock'
  switch (key) {
    case 'stride': return new StridePipelineService()
    case 'mock':
    default:          return new MockPipelineService()
  }
}

const defaultService = resolveService()

// ── Context ───────────────────────────────────────────────────────────────────

const PipelineServiceContext = createContext<IPipelineService>(defaultService)

interface PipelineServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: IPipelineService
}

export function PipelineServiceProvider({
  children,
  service: override,
}: PipelineServiceProviderProps) {
  return (
    <PipelineServiceContext.Provider value={override ?? defaultService}>
      {children}
    </PipelineServiceContext.Provider>
  )
}

export function usePipelineService(): IPipelineService {
  return useContext(PipelineServiceContext)
}
